import React from 'react';

import { isEditWithSource, isLinkElement } from '@aline/common';
import { link } from '@aline/routes';
import { Paper } from '@mui/material';
import clsx from 'clsx';
import { ImagePlus, Link2, MessageCirclePlus, PinIcon, Sparkle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Editor } from 'slate';
import { AlineTooltip, defaultEditorPopoverPaperStyles, SelectButton } from 'src/components';
import { $chatCallbacks } from 'src/components/ai/chat/chat-state';
import { CheckIcon } from 'src/components/icons/CheckIcon';
import { RejectChangeXIcon } from 'src/components/icons/RejectChangeXIcon';
import { imageDialogStateVar } from 'src/editor/AlineEditor';
import { useSetEditingLinkOpen } from 'src/editor/popovers';
import { Queries } from 'src/editor/queries';
import { useOpenComments } from 'src/editor/toolbars/right-panel';
import { attachInsertLinkPopper } from 'src/editor/toolbars/tabs/insert-tab';
import {
  useGetSelectedSuggestedEdits,
  useTrackedChangesAPI,
} from 'src/editor/toolbars/tabs/review-tab';
import {
  useMakeTableRectangularAction,
  useTableCellMergeSplitActions,
} from 'src/editor/toolbars/tabs/table-tab';
import { ClientTransforms } from 'src/editor/transforms';
import { useCurrentUserId } from 'src/gql';
import { useDocumentParams, useIsAtVersionHistoryRoute } from 'src/helpers';
import { useGetEditorUserState } from 'src/hooks';
import { useEditor, useSlateSelector } from 'src/layout-engine';
import { useIsFocusedView } from 'src/layouts/focused-view';
import { useGlobalDrawer } from 'src/layouts/GlobalRightDrawer';
import { useCurrentUserPermissionRole } from 'src/store/document/selectors';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  aiChatButton: {
    alignItems: 'center',
    color: theme.palette.primary.main,
    display: 'flex',
    gap: theme.spacing(1),
    height: '100%',
    padding: theme.spacing(1),
  },
  aiChatButtonIcon: {
    display: 'flex',
    margin: theme.spacing(0, 1),
  },
  button: {
    height: '100%',
    padding: theme.spacing(1),
  },
  buttonAccept: {
    color: theme.appColors.acceptSuggestion,
  },
  buttonKeep: {
    color: theme.appColors.keepSuggestions,
  },
  buttonReject: {
    color: theme.appColors.rejectSuggestion,
  },
  divider: {
    backgroundColor: theme.palette.divider,
    height: 16,
    margin: theme.spacing(0, 1),
    width: 1,
  },
  fullHeight: {
    height: '100%',
  },
  icon: {
    height: 20,
    width: 20,
  },
  paper: {
    '& svg:not(.MuiSvgIcon-root)': {
      height: 22,
      width: 22,
    },
    ...defaultEditorPopoverPaperStyles,
    alignItems: 'center',
    borderRadius: 8,
    display: 'flex',
    maxWidth: 'unset',
    minWidth: 'unset',
  },
}));

export function SelectionRibbon(): JSX.Element | null {
  const editor = useEditor();
  const { classes } = useStyles();
  const navigate = useNavigate();
  const isFocused = useIsFocusedView();
  const { openDrawer } = useGlobalDrawer();
  const { documentId } = useDocumentParams();
  const { openAddComment } = useOpenComments();
  const { setEditingLinkOpen } = useSetEditingLinkOpen();
  const { isAtVersionHistoryRoute } = useIsAtVersionHistoryRoute();
  // isReadOnly === true if doc is signed.
  const { isInSigningCeremony, isReadOnly } = useGetEditorUserState();
  const isInsertLinkDisabled = useSlateSelector(editor => {
    if (!editor.selection) return true;
    return Queries.rangeIncludesCheckboxElement(editor, editor.selection);
  });

  function handleClickLink(): void {
    const linkEntry = Editor.above(editor, { match: isLinkElement });
    if (linkEntry) return setEditingLinkOpen(linkEntry[1]);
    attachInsertLinkPopper(editor);
  }

  function handleOpenAIChat(): void {
    /**
     * Snapshot the live selection now — between this click and the
     * setTimeout firing, focus moves to the chat drawer and `editor.selection`
     * can be cleared.
     */
    const range = editor.selection;
    if (!range) return;

    // Navigate to AI route
    navigate(link.DocumentDetail({ documentId }), { replace: true });
    openDrawer();
    setTimeout(() => {
      $chatCallbacks.insertSelectionMentionV3?.(editor, range);
    }, 0);
  }

  if (isInSigningCeremony || isReadOnly || isAtVersionHistoryRoute) return null;

  return (
    <Paper className={classes.paper}>
      {!isFocused && (
        <>
          <div className={classes.fullHeight}>
            <SelectButton
              className={classes.aiChatButton}
              color="primary"
              onClick={handleOpenAIChat}
            >
              <span className={classes.aiChatButtonIcon}>
                <Sparkle strokeWidth={1.5} />
              </span>
              Add to Chat
            </SelectButton>
          </div>
          <div className={classes.divider} />
        </>
      )}
      <AlineTooltip arrow placement="top" title="Add comment">
        <div className={classes.fullHeight}>
          <SelectButton className={classes.button} color="primary" onClick={openAddComment}>
            <MessageCirclePlus strokeWidth={1.5} />
          </SelectButton>
        </div>
      </AlineTooltip>
      <AlineTooltip arrow placement="top" title="Insert link">
        <div className={classes.fullHeight}>
          <SelectButton
            className={classes.button}
            color="primary"
            disabled={isInsertLinkDisabled}
            onClick={handleClickLink}
          >
            <Link2 strokeWidth={1.5} />
          </SelectButton>
        </div>
      </AlineTooltip>
      <AlineTooltip arrow placement="top" title="Insert image">
        <div className={classes.fullHeight}>
          <SelectButton
            className={classes.button}
            color="primary"
            onClick={() => imageDialogStateVar({ open: true })}
          >
            <ImagePlus strokeWidth={1.5} />
          </SelectButton>
        </div>
      </AlineTooltip>
      <TableActionButtons />
      <TrackedChangesButtons />
    </Paper>
  );
}

function TrackedChangesButtons(): JSX.Element | null {
  const editor = useEditor();
  const { classes } = useStyles();
  const { canSuggest } = useCurrentUserPermissionRole();
  const currentUserId = useCurrentUserId();
  const { acceptOrRejectSelectedChanges, keepSelectedChanges } = useTrackedChangesAPI();
  const { selectedSuggestedEdits } = useGetSelectedSuggestedEdits();
  const disabledReason = !canSuggest
    ? 'Only Editor and Suggesters can accept or reject changes'
    : '';
  const hasCurrentUserAiEdits = selectedSuggestedEdits.some(
    editEntry => isEditWithSource(editEntry.edit) && editEntry.edit.authorId === currentUserId
  );

  if (!selectedSuggestedEdits.length) return null;

  return (
    <>
      <div className={classes.divider} />
      <AlineTooltip arrow placement="top" title={disabledReason || 'Accept changes'}>
        <div className={classes.fullHeight}>
          <SelectButton
            className={clsx(classes.button, classes.buttonAccept)}
            disabled={Boolean(disabledReason)}
            onClick={() => {
              acceptOrRejectSelectedChanges('accept');
              ClientTransforms.focus(editor);
            }}
          >
            <CheckIcon />
          </SelectButton>
        </div>
      </AlineTooltip>
      <AlineTooltip arrow placement="top" title={disabledReason || 'Reject changes'}>
        <div className={classes.fullHeight}>
          <SelectButton
            className={clsx(classes.button, classes.buttonReject)}
            disabled={Boolean(disabledReason)}
            onClick={() => {
              acceptOrRejectSelectedChanges('reject');
              ClientTransforms.focus(editor);
            }}
          >
            <RejectChangeXIcon />
          </SelectButton>
        </div>
      </AlineTooltip>
      {hasCurrentUserAiEdits && (
        <AlineTooltip arrow placement="top" title={disabledReason || 'Keep redlines'}>
          <div className={classes.fullHeight}>
            <SelectButton
              className={clsx(classes.button, classes.buttonKeep)}
              disabled={Boolean(disabledReason)}
              onClick={() => {
                keepSelectedChanges();
                ClientTransforms.focus(editor);
              }}
            >
              <PinIcon size={20} />
            </SelectButton>
          </div>
        </AlineTooltip>
      )}
    </>
  );
}

function TableActionButtons(): JSX.Element | null {
  const { classes } = useStyles();
  const isSelectionWithinTable = useSlateSelector(
    editor => Queries.getIsSelectionWithinTable(editor).isSelectionWithinTable
  );
  const tableActions = [...useTableCellMergeSplitActions(), useMakeTableRectangularAction()].filter(
    ({ visible }) => visible
  );

  if (!isSelectionWithinTable) return null;

  return (
    <>
      {tableActions.map(({ enabled, icon: Icon, key, label, onClick }) => (
        <AlineTooltip key={key} arrow placement="top" title={label}>
          <div className={classes.fullHeight}>
            <SelectButton
              aria-label={label}
              className={classes.button}
              color="primary"
              disabled={!enabled}
              onClick={onClick}
            >
              <Icon strokeWidth={1.5} />
            </SelectButton>
          </div>
        </AlineTooltip>
      ))}
    </>
  );
}
